import type { BarIssues, Judgement, NoteResult } from '@corocanto/scoring';
import { noteColorKey } from './noteKey';

type T = (key: string, options?: Record<string, unknown>) => string;

/** Note colors on the results score: green, yellow, red (missed notes are red too). */
export const STATUS_COLORS: Record<Judgement | 'missed', string> = {
  good: '#2E9E5B',
  close: '#D99A12',
  wrong: '#C0392B',
  missed: '#C0392B',
};

/** A short description (in the current language) of what went wrong in a bar, e.g. "2 nota pes (ortalama −38 sent); 1 geç giriş". */
export function describeIssues(issues: BarIssues, t: T): string {
  const parts: string[] = [];
  if (issues.missed) parts.push(t('results.issues.missed', { count: issues.missed }));
  if (issues.flat) parts.push(t('results.issues.flat', { count: issues.flat, cents: issues.flatCents }));
  if (issues.sharp) parts.push(t('results.issues.sharp', { count: issues.sharp, cents: `+${issues.sharpCents}` }));
  if (issues.octave) parts.push(t('results.issues.octave', { count: issues.octave }));
  if (issues.late) parts.push(t('results.issues.late', { count: issues.late }));
  if (issues.early) parts.push(t('results.issues.early', { count: issues.early }));
  return parts.join('; ');
}

/** The bars to loop around a weak bar: one before and one after, inside the song. */
export function loopAround(measureIndex: number, lastIndex: number): { from: number; to: number } {
  return { from: Math.max(0, measureIndex - 1), to: Math.min(lastIndex, measureIndex + 1) };
}

export type MainIssue = 'missed' | 'octave' | 'flat' | 'sharp' | 'late' | 'early';

/** The one issue to name for a weak bar in a short label ("17. ölçü · pes"): the most frequent. */
export function mainIssue(issues: BarIssues): MainIssue | null {
  const order: MainIssue[] = ['missed', 'octave', 'flat', 'sharp', 'late', 'early'];
  let best: MainIssue | null = null;
  for (const key of order) {
    if (issues[key] > 0 && (best === null || issues[key] > issues[best])) best = key;
  }
  return best;
}

/** Note colors for a take's results on the score; a bar sung twice (repeat) shows its worse result. */
export function noteColorsFor(notes: readonly NoteResult[]): Record<string, string> {
  const rank = { good: 0, close: 1, wrong: 2, missed: 3 } as const;
  const worst = new Map<string, NoteResult['status']>();
  for (const n of notes) {
    const key = noteColorKey(n.partId, n.measureIndex, n.beatOffset, n.spelling);
    const prev = worst.get(key);
    if (!prev || rank[n.status] > rank[prev]) worst.set(key, n.status);
  }
  return Object.fromEntries([...worst].map(([k, s]) => [k, STATUS_COLORS[s]]));
}
